import React, { useCallback, useEffect, useMemo } from 'react'
import { ICartOrder } from 'order_components/IOrder'
import {
  reqAddProductToCart,
  reqChangeCartProductQuantity,
  reqGetCartOrder,
  reqRemoveProductFromCart,
  reqUpdateNewOrderPrarmeter,
} from 'cart/CartRequests'
import { IProductBase } from 'product/IProduct'
import { useAuth } from 'auth/AuthProvider'

type TCartActCallback = (success?: boolean, message?: string) => void

interface ICartActions {
  addProduct: (product: number | IProductBase, callback?: TCartActCallback) => void
  removeProduct: (product: number | IProductBase, callback?: TCartActCallback) => void
  changeProductQuantity: (
    product: number | IProductBase,
    newQuantity: number,
    callback?: TCartActCallback
  ) => void
  updatePrarmeter: (
    key: string,
    value: number | string | null,
    callback?: TCartActCallback
  ) => void
  refresh: () => void
}

type CartContextType = [ICartOrder | null, ICartActions]

let CartContext = React.createContext<CartContextType>(null!)

function CartProvider({ children }: { children: React.ReactNode }) {
  const auth = useAuth()
  const [order, setOrder] = React.useState<any | null>(null)

  useEffect(() => {
    reqGetCartOrder(setOrder)
  }, [auth.user])

  const addProduct = useCallback(
    (product: number | IProductBase, callback?: TCartActCallback) => {
      const productId = typeof product === 'number' ? product : product.product_id
      reqAddProductToCart(productId, (order) => {
        setOrder(order)
        callback && callback(true)
      })
    },
    []
  )

  const removeProduct = useCallback(
    (product: number | IProductBase, callback?: TCartActCallback) => {
      const productId = typeof product === 'number' ? product : product.product_id
      reqRemoveProductFromCart(productId, (newOrder) => {
        setOrder(newOrder)
        callback && callback(true)
      })
    },
    []
  )

  const changeProductQuantity = useCallback(
    (product: number | IProductBase, newQuantity, callback?: TCartActCallback) => {
      const productId = typeof product === 'number' ? product : product.product_id
      reqChangeCartProductQuantity(productId, newQuantity, (newOrder) => {
        setOrder(newOrder)
        callback && callback(true)
      })
    },
    []
  )

  const updatePrarmeter = useCallback(
    (
      // orderId: number,
      key: string,
      value: number | string | null,
      callback?: TCartActCallback
    ) => {
      reqUpdateNewOrderPrarmeter(order.order_id, key, value, (newOrder) => {
        setOrder(newOrder)
        callback && callback(true)
      })
    },
    [order && order.order_id]
  )

  const refresh = useCallback(() => {
    reqGetCartOrder(setOrder)
  }, [])

  const value = [
    order,
    // { addProduct, removeProduct, clear },
    useMemo(() => {
      return {
        addProduct,
        removeProduct,
        changeProductQuantity,
        updatePrarmeter,
        refresh,
      }
    }, [addProduct, removeProduct, changeProductQuantity, updatePrarmeter, refresh]),
  ]

  return (
    <CartContext.Provider value={value as CartContextType}>
      {children}
    </CartContext.Provider>
  )
}

function useCart() {
  return React.useContext(CartContext)
}

export { CartProvider, useCart, CartContext }
